import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { purchaseApi, inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-PUR-04.01 / 04.02 — Return-to-vendor (RTV).
 *
 * Approval flow: draft → approved → shipped → completed.
 * Approval is hierarchical (creator can't approve own); shipping posts
 * Dr AP / Cr Inventory.
 */
export function RtvPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useQuery({
    queryKey: ['purchase', 'rtvs', statusFilter],
    queryFn: () => purchaseApi.listRtvs({ status: statusFilter || undefined }),
  });

  const kpis = (() => {
    const rows = listQuery.data ?? [];
    let draft = 0, approved = 0, shipped = 0, totalValue = 0;
    for (const r of rows) {
      if (r.status === 'draft') draft++;
      if (r.status === 'approved') approved++;
      if (r.status === 'shipped') shipped++;
      totalValue += parseFloat(r.total_value) || 0;
    }
    return { total: rows.length, draft, approved, shipped, totalValue };
  })();

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'approved',  label: 'Approved' },
    { value: 'shipped',   label: 'Shipped' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Purchase"
        title="Return to vendor"
        subtitle="Send purchased stock back to suppliers with an AP credit. Requires approval; shipping posts Dr AP / Cr Inventory."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New RTV
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Awaiting approval" value={kpis.draft}
          accent={kpis.draft > 0 ? 'amber' : 'default'} />
        <KpiTile label="Ready to ship" value={kpis.approved}
          accent={kpis.approved > 0 ? 'sage' : 'default'} />
        <KpiTile label="Total return value" value={`${fmtCurrency(kpis.totalValue)}`} />
      </div>

      {/* Status chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_32rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
            !listQuery.data?.length ? (
              <EmptyState
                message={statusFilter ? `No ${statusFilter} RTVs` : 'No RTVs yet'}
                hint={statusFilter ? 'Try a different filter.' : 'Create an RTV to return stock to a supplier.'}
                action={!statusFilter && (
                  <button className="btn-primary" onClick={() => setShowCreate(true)}>
                    + New RTV
                  </button>
                )}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>RTV #</th>
                      <th>Supplier</th>
                      <th>Status</th>
                      <th className="text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(listQuery.data ?? []).map((r: any) => (
                      <tr key={r.id}
                          onClick={() => setSelected(r.id)}
                          className={`cursor-pointer ${selected === r.id ? '!bg-canvas-subtle' : ''}`}>
                        <td className="font-mono text-xs font-semibold text-ink-900">{r.rtv_number}</td>
                        <td className="text-sm text-ink-700">{r.supplier_name}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                          ${parseFloat(r.total_value).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select an RTV"
              hint="Click a row on the left to view return items and approve/ship/complete actions."
            />
          ) : (
            <RtvDetail rtvId={selected} onChanged={() => qc.invalidateQueries({ queryKey: ['purchase', 'rtvs'] })} />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateRtvModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['purchase', 'rtvs'] });
            setShowCreate(false);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    completed:        'bg-sage-50 text-sage-800 ring-sage-200',
    shipped:          'bg-blue-50 text-blue-700 ring-blue-200',
    approved:         'bg-sage-50 text-sage-700 ring-sage-200',
    draft:            'bg-canvas-subtle text-ink-700 ring-ink-200',
    cancelled:        'bg-red-50 text-red-700 ring-red-200',
    pending_approval: 'bg-amber-50 text-amber-800 ring-amber-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function RtvDetail({ rtvId, onChanged }: { rtvId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['purchase', 'rtv', rtvId], queryFn: () => purchaseApi.getRtv(rtvId) });

  const [showReject, setShowReject] = useState(false);

  const decideMut = useMutation({
    mutationFn: ({ approve, reason }: { approve: boolean; reason?: string }) => purchaseApi.decideRtv(rtvId, approve, reason),
    onSuccess: (d) => {
      toast.success(`RTV ${d.status}`);
      qc.invalidateQueries({ queryKey: ['purchase', 'rtv', rtvId] });
      onChanged();
      setShowReject(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const shipMut = useMutation({
    mutationFn: () => purchaseApi.shipRtv(rtvId),
    onSuccess: (d) => { toast.success(`Shipped. ${fmtCurrency(d.shippedValue ?? 0)} back to vendor.`); qc.invalidateQueries({ queryKey: ['purchase', 'rtv', rtvId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const completeMut = useMutation({
    mutationFn: () => purchaseApi.completeRtv(rtvId),
    onSuccess: () => { toast.success('Completed'); qc.invalidateQueries({ queryKey: ['purchase', 'rtv', rtvId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const r = q.data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{r.rtv_number}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">{r.supplier_name}</div>
          <div className="text-xs text-ink-500 mt-1">→ {r.warehouse_code}</div>
          {r.source_po_number && (
            <div className="text-xs text-ink-500 mt-0.5">
              From PO <span className="font-mono font-semibold text-ink-700">{r.source_po_number}</span>
            </div>
          )}
          {r.source_grn_number && (
            <div className="text-xs text-ink-500 mt-0.5">
              From GRN <span className="font-mono font-semibold text-ink-700">{r.source_grn_number}</span>
            </div>
          )}
          {r.reason && <div className="text-xs text-ink-500 italic mt-1 leading-relaxed">{r.reason}</div>}
        </div>
        <StatusBadge status={r.status} />
      </div>

      {/* Timeline */}
      {(r.approved_at || r.shipped_at || r.completed_at || r.journal_entry_number) && (
        <div className="bg-canvas-subtle rounded-xl p-3 mb-4 space-y-1 text-xs text-ink-600">
          {r.approved_at && (
            <div>
              <span className="text-ink-400">Approved:</span>{' '}
              {new Date(r.approved_at).toLocaleString()} · {r.approved_by_name}
            </div>
          )}
          {r.shipped_at && (
            <div>
              <span className="text-ink-400">Shipped:</span>{' '}
              {new Date(r.shipped_at).toLocaleString()} · {r.shipped_by_name}
            </div>
          )}
          {r.completed_at && (
            <div className="text-sage-700">
              <span className="text-sage-600">Completed:</span>{' '}
              {new Date(r.completed_at).toLocaleString()}
            </div>
          )}
          {r.journal_entry_number && (
            <div>
              <span className="text-ink-400">JE:</span>{' '}
              <span className="font-mono font-semibold">{r.journal_entry_number}</span>
            </div>
          )}
        </div>
      )}

      {/* Line items */}
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit cost</th>
              <th className="text-right">Line</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {r.lines.map((l: any) => (
              <tr key={l.id}>
                <td>
                  <div className="font-medium text-ink-900">{l.product_name}</div>
                  <div className="font-mono text-2xs text-ink-500 mt-0.5">{l.sku}</div>
                </td>
                <td className="text-right font-mono tabular-nums">{parseFloat(l.quantity).toFixed(0)}</td>
                <td className="text-right font-mono tabular-nums">{fmtCurrency(parseFloat(l.unit_cost))}</td>
                <td className="text-right font-mono font-semibold tabular-nums">
                  ${(parseFloat(l.quantity) * parseFloat(l.unit_cost)).toFixed(2)}
                </td>
                <td className="text-2xs text-ink-600 font-mono">
                  {l.reason_code ?? <span className="text-ink-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Total strip */}
      <div className="bg-canvas-subtle rounded-xl p-4 mb-4">
        <div className="flex justify-between items-baseline">
          <span className="font-semibold text-ink-900">Total</span>
          <span className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight">
            ${parseFloat(r.total_value).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {(r.status === 'draft' || r.status === 'pending_approval') && (
          <>
            <button className="btn-secondary" disabled={decideMut.isPending}
              onClick={() => setShowReject(true)}>
              Reject
            </button>
            <button className="btn-primary" disabled={decideMut.isPending}
              onClick={() => decideMut.mutate({ approve: true })}>
              {decideMut.isPending ? 'Approving…' : 'Approve'}
            </button>
          </>
        )}
        {r.status === 'approved' && (
          <button className="btn-primary" disabled={shipMut.isPending} onClick={() => shipMut.mutate()}>
            {shipMut.isPending ? 'Shipping…' : 'Ship to supplier'}
          </button>
        )}
        {r.status === 'shipped' && (
          <button className="btn-primary" disabled={completeMut.isPending} onClick={() => completeMut.mutate()}>
            {completeMut.isPending ? 'Completing…' : 'Mark completed'}
          </button>
        )}
      </div>

      {showReject && (
        <RejectRtvDialog
          rtvNumber={r.rtv_number}
          loading={decideMut.isPending}
          onClose={() => setShowReject(false)}
          onConfirm={(reason) => decideMut.mutate({ approve: false, reason })}
        />
      )}
    </div>
  );
}

function RejectRtvDialog({
  rtvNumber, loading, onClose, onConfirm,
}: { rtvNumber: string; loading: boolean; onClose: () => void; onConfirm: (reason: string | undefined) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Purchase · Reject</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject return to vendor?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Reject <span className="font-mono font-semibold text-ink-900">{rtvNumber}</span>?
          The RTV stays on file for audit but no stock movements or GL postings happen.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className="input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Credit already processed via different route" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={() => onConfirm(reason.trim() || undefined)}>
          {loading ? 'Rejecting…' : 'Reject RTV'}
        </button>
      </div>
    </Modal>
  );
}

function CreateRtvModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<Array<any>>([]);
  const [productSearch, setProductSearch] = useState('');

  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });
  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const prodQuery = useQuery({
    queryKey: ['products-search-rtv', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });

  const mut = useMutation({
    mutationFn: () => purchaseApi.createRtv({
      supplierId, warehouseId, reason: reason || undefined,
      lines: lines.map((l) => ({
        productId: l.productId, quantity: l.quantity, unitCost: l.unitCost,
        reasonCode: l.reasonCode,
      })),
    }),
    onSuccess: (d) => { toast.success(`RTV ${d.rtvNumber} created as draft`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = supplierId && warehouseId && lines.length > 0 &&
    lines.every((l) => l.quantity > 0 && l.unitCost > 0);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Purchase · New</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New return to vendor</h2>
        <p className="text-sm text-ink-500 mt-1">
          Send goods back to a supplier. Creates a draft — approving and shipping posts stock movements and GL entries.
        </p>
      </div>

      <div className="px-6 pt-4 pb-3 grid grid-cols-2 gap-3 flex-shrink-0">
        <div>
          <label className="label">Supplier <span className="text-danger">*</span></label>
          <select className="input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Select…</option>
            {supQuery.data?.map((s: any) => <option key={s.id} value={s.id}>{s.code} — {s.legal_name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Warehouse (shipping from) <span className="text-danger">*</span></label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select…</option>
            {whQuery.data?.map((w: any) => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
          </select>
        </div>
      </div>

      <div className="px-6 pb-3 flex-shrink-0">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Summary reason for this return" />
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="eyebrow text-ink-500 mb-2">Lines</div>
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <div className="p-3 border-b border-ink-100 bg-canvas-subtle/60">
            <input className="input text-sm" placeholder="Search product to add…"
              value={productSearch} onChange={(e) => setProductSearch(e.target.value)} />
            {prodQuery.data?.data?.length > 0 && productSearch.length >= 2 && (
              <div className="mt-2 max-h-40 overflow-y-auto bg-canvas-elevated border border-ink-100 rounded-lg">
                {(prodQuery.data?.data ?? []).map((p: any) => (
                  <button key={p.id}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-canvas-subtle transition-colors border-b border-ink-100 last:border-b-0"
                    onClick={() => {
                      setLines((prev) => [...prev, {
                        productId: p.id, productName: p.name, sku: p.sku,
                        quantity: 1, unitCost: parseFloat(p.cost_price) || 0,
                        reasonCode: 'defective',
                      }]);
                      setProductSearch('');
                    }}>
                    <span className="font-mono font-semibold text-ink-900">{p.sku}</span>
                    <span className="text-ink-500 ml-2">{p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {lines.length === 0 ? (
            <div className="p-8 text-center text-xs text-ink-400">
              Search above to add products to this return.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right w-20">Qty</th>
                  <th className="text-right w-24">Cost</th>
                  <th>Reason</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <div className="font-medium text-ink-900 text-xs">{l.productName}</div>
                      <div className="font-mono text-2xs text-ink-500 mt-0.5">{l.sku}</div>
                    </td>
                    <td className="text-right">
                      <input type="number" min="0.01" step="1"
                        className="input w-20 text-right tabular-nums" value={l.quantity}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} />
                    </td>
                    <td className="text-right">
                      <input type="number" min="0.01" step="0.01"
                        className="input w-24 text-right tabular-nums" value={l.unitCost}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitCost: parseFloat(e.target.value) || 0 } : x))} />
                    </td>
                    <td>
                      <select className="input text-xs" value={l.reasonCode ?? 'defective'}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, reasonCode: e.target.value } : x))}>
                        <option value="defective">Defective</option>
                        <option value="wrong_item">Wrong item</option>
                        <option value="over_shipment">Over-shipment</option>
                        <option value="other">Other</option>
                      </select>
                    </td>
                    <td>
                      <button
                        className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                        onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                        aria-label="Remove line"
                      >✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create RTV'}
        </button>
      </div>
    </Modal>
  );
}
